import { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';

const API = 'https://snap2excel-backend.onrender.com';

const PLAN_OPTIONS = [
  { value: 'free',   label: 'Free' },
  { value: 'pw_099', label: 'Personal – £0.99/week' },
  { value: 'pm_299', label: 'Personal – £2.99/month' },
  { value: 'bw_149', label: 'Business – £1.49/week' },
  { value: 'bm_349', label: 'Business – £3.49/month' },
];

function readablePlan(type) {
  const found = PLAN_OPTIONS.find((p) => p.value === type);
  return found ? found.label : (type || 'Unknown');
}

function Admin({ token }) {
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [userStats, setUserStats] = useState(null);
  const [sevenDay, setSevenDay] = useState(null);
  const [retention, setRetention] = useState(null);

  const [dau, setDau] = useState(null);
  const [activeUsers, setActiveUsers] = useState(null);
  const [activity, setActivity] = useState([]);

  const authAxios = axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` },
  });

  const loadData = async () => {
    try {
      const [sRes, uRes, statsRes, sevenRes, retRes, dauRes, activeRes, actRes] =
        await Promise.all([
          authAxios.get('/api/admin/summary'),
          authAxios.get('/api/admin/users'),
          authAxios.get('/api/admin/user-stats'),
          authAxios.get('/api/admin/7day'),
          authAxios.get('/api/admin/retention'),
          authAxios.get('/api/admin/dau'),
          authAxios.get('/api/admin/active-users'),
          authAxios.get('/api/admin/activity'),
        ]);

      setSummary(sRes.data);
      setUsers(uRes.data);
      setUserStats(statsRes.data);
      setSevenDay(sevenRes.data);
      setRetention(retRes.data);
      setDau(dauRes.data);
      setActiveUsers(activeRes.data);
      setActivity(actRes.data || []);
      setSevenDay(sevenRes.data);
setRetention(retRes.data);
setDau(dauRes.data);
setActiveUsers(activeRes.data);
setActivity(Array.isArray(actRes.data) ? actRes.data : []);

    } catch (err) {
      console.error('Admin load error', err);
      alert('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
  loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  const updatePlan = async (userId, plan_type) => {
    try {
      await authAxios.post(`/api/admin/users/${userId}/plan`, {
        plan_type,
      });
      await loadData();
    } catch (err) {
      console.error('Admin update plan error', err);
      alert('Failed to update plan');
    }
  };

  const toggleBan = async (user) => {
    try {
      await authAxios.post(`/api/admin/users/${user.id}/ban`, {
        banned: user.banned ? 0 : 1,
      });
      await loadData();
    } catch (err) {
      console.error('Admin ban error', err);
      alert('Failed to update ban');
    }
  };

  const toggleAdmin = async (user) => {
    try {
      await authAxios.post(`/api/admin/users/${user.id}/admin`, {
        is_admin: user.is_admin ? 0 : 1,
      });
      await loadData();
    } catch (err) {
      console.error('Admin admin toggle error', err);
      alert('Failed to update admin flag');
    }
  };

  const adjustCredits = async (user) => {
    const input = window.prompt(
      `Adjust credits for ${user.email} (current: ${user.credits}).\n` +
        'Enter a positive or negative number (e.g. 5 or -5):'
    );
    if (input === null) return;

    const delta = Number(input);
    if (Number.isNaN(delta)) {
      alert('Invalid number');
      return;
    }

    try {
      await authAxios.post(`/api/admin/users/${user.id}/credits`, { delta });
      await loadData();
    } catch (err) {
      console.error('Admin credits error', err);
      alert('Failed to adjust credits');
    }
  };

  // Export helpers – use axios so JWT header is sent
  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const exportUsersCsv = async () => {
    try {
      const res = await authAxios.get('/api/admin/export/users', {
        responseType: 'blob',
      });
      downloadBlob(res.data, 'users.csv');
    } catch (err) {
      console.error('Export users error', err);
      alert('Failed to export users CSV');
    }
  };

  const exportReceiptsCsv = async () => {
    try {
      const res = await authAxios.get('/api/admin/export/receipts', {
        responseType: 'blob',
      });
      downloadBlob(res.data, 'receipts.csv');
    } catch (err) {
      console.error('Export receipts error', err);
      alert('Failed to export receipts CSV');
    }
  };

  if (loading) {
    return (
      <div className="app-root">
        <div className="app-container">Loading admin…</div>
      </div>
    );
  }

  return (
    <div className="app-root">
      <div className="app-container">
        {/* HEADER */}
        <header className="app-header">
          <div>
            <h1 className="app-title">Admin dashboard</h1>
            <p className="app-subtitle">
              Manage Snap2Excel users, plans, and usage.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className="btn btn-secondary"
              onClick={loadData}
              style={{ height: 40 }}
            >
              Refresh
            </button>

            <button
              className="btn btn-secondary"
              style={{ height: 40 }}
              onClick={exportUsersCsv}
            >
              Export users CSV
            </button>

            <button
              className="btn btn-secondary"
              style={{ height: 40 }}
              onClick={exportReceiptsCsv}
            >
              Export receipts CSV
            </button>

            <button
              className="btn"
              style={{ height: 40, background: '#0ea5e9', color: '#fff' }}
              onClick={() => (window.location.href = '/app')}
            >
              ← Back to app
            </button>
          </div>
        </header>

        {/* TOP: user stats (from /user-stats) */}
        {userStats && (
          <div className="dashboard-row" style={{ marginBottom: 20 }}>
            <div className="dashboard-card">
              <h3>Total users</h3>
              <strong>{userStats.total_users}</strong>
            </div>
            <div className="dashboard-card">
              <h3>Pro users</h3>
              <strong>{userStats.pro_users}</strong>
            </div>
            <div className="dashboard-card">
              <h3>Free users</h3>
              <strong>{userStats.free_users}</strong>
            </div>
          </div>
        )}

        {/* SUMMARY FROM /summary – revenue + receipts */}
        {summary && (
          <div className="dashboard-row">
            <div className="dashboard-card">
              <h3>Total users (summary)</h3>
              <strong>{summary.total_users}</strong>
            </div>
            <div className="dashboard-card">
              <h3>Pro users (summary)</h3>
              <strong>{summary.pro_users}</strong>
            </div>
            <div className="dashboard-card">
              <h3>Weekly MRR (est)</h3>
              <strong>
                £{Number(summary.weekly_mrr || 0).toFixed(2)}
              </strong>
            </div>
            <div className="dashboard-card">
              <h3>Total receipts</h3>
              <strong>{summary.total_receipts}</strong>
            </div>
            <div className="dashboard-card">
              <h3>Total spend tracked</h3>
              <strong>
                £{Number(summary.total_spend || 0).toFixed(2)}
              </strong>
            </div>
          </div>
        )}

        {/* 7-day metrics & retention */}
        {(sevenDay || retention) && (
          <div className="dashboard-row">
            {sevenDay && (
              <div className="dashboard-card">
                <h3>Last 7 days</h3>
                <p style={{ margin: 0, fontSize: 13 }}>
                  New users:{' '}
                  <strong>{sevenDay.new_users ?? 0}</strong>
                </p>
                <p style={{ margin: 0, fontSize: 13 }}>
                  Receipts scanned:{' '}
                  <strong>{sevenDay.receipts ?? 0}</strong>
                </p>
              </div>
            )}
            {retention && (
              <div className="dashboard-card">
                <h3>Retention</h3>
                <p style={{ margin: 0, fontSize: 13 }}>
                  7-day returning:{' '}
                  <strong>{retention.returning_7d ?? 0}</strong>
                </p>
                <p style={{ margin: 0, fontSize: 13 }}>
                  30-day returning:{' '}
                  <strong>{retention.returning_30d ?? 0}</strong>
                </p>
              </div>
            )}

            {dau && (
              <div className="dashboard-card">
                <h3>Daily active users</h3>
                <p style={{ margin: 0, fontSize: 13 }}>
                  Today:{' '}
                  <strong>{dau.today ?? 0}</strong>
                </p>
                <p style={{ margin: 0, fontSize: 13 }}>
                  Yesterday:{' '}
                  <strong>{dau.yesterday ?? 0}</strong>
                </p>
                <p style={{ margin: 0, fontSize: 13 }}>
                  Avg last 7 days:{' '}
                  <strong>
                    {dau.avg_7_days != null
                      ? dau.avg_7_days.toFixed(1)
                      : '0.0'}
                  </strong>
                </p>
              </div>
            )}

            {activeUsers && (
              <div className="dashboard-card">
                <h3>Active users</h3>
                <p style={{ margin: 0, fontSize: 13 }}>
                  WAU:{' '}
                  <strong>{activeUsers.wau ?? 0}</strong>
                </p>
                <p style={{ margin: 0, fontSize: 13 }}>
                  MAU:{' '}
                  <strong>{activeUsers.mau ?? 0}</strong>
                </p>
              </div>
            )}
          </div>
        )}

        {/* USERS TABLE */}
        <h2 className="section-title" style={{ marginTop: '2rem' }}>
          Users
        </h2>
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Email</th>
                <th>Plan</th>
                <th>Receipts</th>
                <th>Total spend</th>
                <th>Credits</th>
                <th>Sub status</th>
                <th>Admin</th>
                <th>Banned</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
  {users.map((u) => (
    <tr
      key={u.id}
      className={u.is_admin ? 'table-row table-row--admin' : 'table-row'}
    >
      <td>{u.id}</td>
      <td>{u.email}</td>

      {/* Plan pill */}
      <td>
        <span
          className={`pill pill--plan ${
            u.plan_type && u.plan_type !== 'free'
              ? 'pill--pro'
              : 'pill--free'
          }`}
        >
          {readablePlan(u.plan_type)}
        </span>
      </td>

      <td>{u.receipt_count}</td>
      <td>£{Number(u.total_spend || 0).toFixed(2)}</td>
      <td>{u.credits}</td>
      <td>{u.subscription_status || 'none'}</td>

      {/* Admin badge */}
      <td>
        <span
          className={`pill pill--status ${
            u.is_admin ? 'pill--admin' : 'pill--muted'
          }`}
        >
          {u.is_admin ? 'Admin' : 'User'}
        </span>
      </td>

      {/* Banned badge */}
      <td>
        <span
          className={`pill pill--status ${
            u.banned ? 'pill--banned' : 'pill--ok'
          }`}
        >
          {u.banned ? 'Banned' : 'OK'}
        </span>
      </td>

      {/* Actions – keep the flex layout we added earlier */}
      <td>
        <div className="user-actions">
          <select
            className="auth-input user-actions__plan"
            value={u.plan_type || 'free'}
            onChange={(e) => updatePlan(u.id, e.target.value)}
          >
            {PLAN_OPTIONS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>

          <button
            className="btn btn-secondary user-actions__btn"
            onClick={() => toggleAdmin(u)}
          >
            {u.is_admin ? 'Remove admin' : 'Make admin'}
          </button>

          <button
            className="btn user-actions__btn user-actions__btn--danger"
            onClick={() => toggleBan(u)}
          >
            {u.banned ? 'Unban' : 'Ban'}
          </button>

          <button
            className="btn btn-secondary user-actions__btn"
            onClick={() => adjustCredits(u)}
          >
            Credits ±
          </button>
        </div>
      </td>
    </tr>
  ))}

  {users.length === 0 && (
    <tr>
      <td colSpan={10} style={{ textAlign: 'center', padding: '1rem' }}>
        No users yet.
      </td>
    </tr>
  )}
</tbody>
          </table>
        </div>

 {/* ACTIVITY FEED */}
<h2 className="section-title" style={{ marginTop: '2rem' }}>
  Recent activity
</h2>

<div className="activity-list">
  {/* Empty state */}
  {(!activity || activity.length === 0) && (
    <p style={{ fontSize: 14, opacity: 0.8 }}>
      No activity yet. Once people start scanning receipts,
      you&apos;ll see them here.
    </p>
  )}

  {/* Only map if activity is a real array with items */}
  {Array.isArray(activity) && activity.length > 0 &&
    activity.map((item) => (
      <div key={item.id} className="activity-item">
        <div className="activity-main">
          <div className="activity-title">
            {item.email} scanned £{Number(item.total || 0).toFixed(2)} at{' '}
            <strong>{item.shop || 'Unknown shop'}</strong>
          </div>
          <div className="activity-meta">
            {item.date || item.ts || 'No date'}
          </div>
        </div>
      </div>
    ))}
</div>
      </div>
    </div>
  );
}

export default Admin;
